import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { sourcesApi } from '@/api/clients/sourcesApi'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import type { Source } from '@/api/types'
import { PageHeader, Card, Button, Field, SelectInput } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { QueryState } from '@/components/common/State'
import { formatDate, daysSince } from '@/components/common/format'

// A source whose crypto inventory hasn't refreshed in this many days is stale —
// its PQC picture may be out of date (operational hygiene signal).
const STALE_DAYS = 30

function isStale(s: Source): boolean {
  const age = daysSince(s.last_ingested_at)
  return age != null && age > STALE_DAYS
}

export default function Sources() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()
  const [assignTarget, setAssignTarget] = useState<Source | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Source | null>(null)

  const sources = useQuery({
    queryKey: ['sources', orgId, 'all'],
    queryFn: () => sourcesApi.listAll(orgId),
    enabled: !!orgId,
  })
  const inventories = useQuery({
    queryKey: ['inventories', orgId],
    queryFn: () => inventoriesApi.list(orgId),
    enabled: !!orgId,
  })

  const invName = useMemo(
    () => new Map((inventories.data ?? []).map((i) => [i.id, i.name])),
    [inventories.data]
  )

  const standalone = (sources.data ?? []).filter((s) => !s.inventory_id)
  const standaloneAssets = standalone.reduce((sum, s) => sum + (s.asset_count ?? 0), 0)
  const staleCount = (sources.data ?? []).filter(isStale).length

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sources', orgId] })
    qc.invalidateQueries({ queryKey: ['summary', orgId] })
    qc.invalidateQueries({ queryKey: ['inventories', orgId] })
  }

  return (
    <div>
      <PageHeader
        title="Sources"
        subtitle="Every imported origin of assets, across all inventories"
      />

      {standalone.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm">
          <span className="material-icons text-warning text-base">info</span>
          <span className="text-text-light dark:text-text-dark">
            <b>{standalone.length}</b> unassigned{' '}
            {standalone.length === 1 ? 'source' : 'sources'} · <b>{standaloneAssets}</b>{' '}
            {standaloneAssets === 1 ? 'asset' : 'assets'} not counted in $-at-risk or compliance.
            Assign them to an inventory to include them.
          </span>
        </div>
      )}

      {staleCount > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm">
          <span className="material-icons text-warning text-base">schedule</span>
          <span className="text-text-light dark:text-text-dark">
            <b>{staleCount}</b> {staleCount === 1 ? 'source' : 'sources'} not refreshed in over{' '}
            {STALE_DAYS} days — their crypto inventory may be out of date. Refresh to re-scan.
          </span>
        </div>
      )}

      <Card className="!p-0">
        <QueryState
          isLoading={sources.isLoading}
          error={sources.error}
          isEmpty={sources.data?.length === 0}
          emptyProps={{
            icon: 'dataset',
            title: 'No sources yet',
            hint: 'Import from a connector or create a source under an inventory, then ingest a CBOM.',
          }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Inventory</th>
                <th className="px-5 py-3 font-medium text-right">Assets</th>
                <th className="px-5 py-3 font-medium">Last ingest</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {sources.data?.map((s) => (
                <tr key={s.id} className="hover:bg-primary/5">
                  <td className="px-5 py-3 font-medium text-text-light dark:text-text-dark">
                    {s.name}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {s.source_type}
                  </td>
                  <td className="px-5 py-3">
                    {s.inventory_id ? (
                      <span className="text-text-light dark:text-text-dark">
                        {invName.get(s.inventory_id) ?? s.inventory_id}
                      </span>
                    ) : (
                      <Badge label="standalone" variant="warning" />
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-subtle-light dark:text-subtle-dark">
                    {s.asset_count ?? 0}
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {s.last_ingested_at ? (
                      <span className="inline-flex items-center gap-2">
                        {formatDate(s.last_ingested_at)}
                        {isStale(s) && <Badge label="stale" variant="warning" />}
                      </span>
                    ) : (s.asset_count ?? 0) === 0 ? (
                      <span className="italic">never ingested</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge
                      label={s.is_active ? 'active' : 'inactive'}
                      variant={s.is_active ? 'success' : 'neutral'}
                    />
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" icon="drive_file_move" onClick={() => setAssignTarget(s)}>
                      Assign
                    </Button>
                    <Button variant="ghost" icon="delete" onClick={() => setDeleteTarget(s)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>

      {assignTarget && (
        <AssignModal
          orgId={orgId}
          source={assignTarget}
          inventories={(inventories.data ?? []).map((i) => ({ id: i.id, name: i.name }))}
          onClose={() => setAssignTarget(null)}
          onDone={() => {
            invalidate()
            setAssignTarget(null)
          }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          orgId={orgId}
          source={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => {
            invalidate()
            setDeleteTarget(null)
          }}
        />
      )}
    </div>
  )
}

function AssignModal({
  orgId,
  source,
  inventories,
  onClose,
  onDone,
}: {
  orgId: string
  source: Source
  inventories: { id: string; name: string }[]
  onClose: () => void
  onDone: () => void
}) {
  const [invId, setInvId] = useState<string>(source.inventory_id ?? '')

  const mutation = useMutation({
    mutationFn: () => sourcesApi.assign(orgId, source.id, invId || null),
    onSuccess: onDone,
  })

  return (
    <Modal
      title={`Assign "${source.name}"`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-subtle-light dark:text-subtle-dark">
          Move this source and its assets to an inventory, or leave it standalone. Standalone assets
          are excluded from $-at-risk and compliance until assigned.
        </p>
        <Field label="Inventory">
          <SelectInput value={invId} onChange={(e) => setInvId(e.target.value)}>
            <option value="">None (standalone)</option>
            {inventories.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </SelectInput>
        </Field>
        {mutation.error && <p className="text-error">{(mutation.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function DeleteModal({
  orgId,
  source,
  onClose,
  onDone,
}: {
  orgId: string
  source: Source
  onClose: () => void
  onDone: () => void
}) {
  const mutation = useMutation({
    mutationFn: () => sourcesApi.removeOrg(orgId, source.id),
    onSuccess: onDone,
  })

  return (
    <Modal
      title={`Remove "${source.name}"`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-text-light dark:text-text-dark">
        <p>
          This permanently deletes the source and its assets and scores. This cannot be undone.
        </p>
        {source.origin === 'discovery' && (
          <div className="p-3 rounded-xl bg-warning/10 text-xs">
            <span className="material-icons text-warning text-sm align-middle mr-1">warning</span>
            Mirrored from qtz-discovery — it will reappear on the next "Sync data" unless removed in
            discovery.
          </div>
        )}
        {mutation.error && <p className="text-error">{(mutation.error as Error).message}</p>}
      </div>
    </Modal>
  )
}
